import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  Clock, ChevronLeft, ChevronRight, Edit, Check, Download, FileSpreadsheet,
  FileText, Star, Loader2, Paperclip, Eye, Trash2, CalendarDays, Ticket,
  AlertTriangle, UserMinus, RotateCcw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { erpApi, formatApiErrorDetail } from "@/lib/erpApi";
import { useErpAuth } from "@/context/ErpAuthContext";
import { MONTHS_RO, DAY_LETTERS, PONTAJ_OPTIONS, classFor } from "./pontajConstants";

const displayName = (e) => `${e.nume || ""} ${e.prenume || ""}`.trim() || e.name || e.email;

export default function PontajTab() {
  const { hasPerm } = useErpAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [employees, setEmployees] = useState([]);
  const [data, setData] = useState({});
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [cell, setCell] = useState(null); // {emp, day, value}
  const [docBusy, setDocBusy] = useState(false);
  const [holidayDialog, setHolidayDialog] = useState(false);
  const [holidayList, setHolidayList] = useState([]);
  const [newHoliday, setNewHoliday] = useState({ zi: "", luna: String(month), nume: "", recurent: true });
  const [tichetDialog, setTichetDialog] = useState(false);
  const [valoareTichet, setValoareTichet] = useState(40);

  const canEdit = hasPerm("pontaj.editare");
  const canExport = hasPerm("pontaj.export");

  const daysInMonth = new Date(year, month, 0).getDate();
  const holidayDays = Object.fromEntries(holidays.map((h) => [h.zi, h.nume]));
  const isCurrentMonth = year === now.getFullYear() && month === now.getMonth() + 1;
  const isFutureMonth = year > now.getFullYear() || (year === now.getFullYear() && month > now.getMonth() + 1);
  const todayDate = now.getDate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = await erpApi.get(`/pontaj/${year}/${month}`);
      setEmployees(p.data.employees || []);
      setData(p.data.pontaj || {});
      setHolidays(p.data.holidays || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const isWeekend = (day) => {
    const d = new Date(year, month - 1, day).getDay();
    return d === 0 || d === 6;
  };
  const isHoliday = (day) => day in holidayDays;

  const dayInMonth = (dateStr, isStart) => {
    if (!dateStr) return null;
    const d = new Date(dateStr.slice(0, 10));
    if (isNaN(d)) return null;
    if (d.getFullYear() === year && d.getMonth() + 1 === month) return d.getDate();
    return null;
  };

  const empInMonth = (emp) => {
    const s = (emp.angajat_de_la || "").slice(0, 10);
    if (s) { const d = new Date(s); if (year < d.getFullYear() || (year === d.getFullYear() && month < d.getMonth() + 1)) return false; }
    const en = (emp.pontaj_pana_la || "").slice(0, 10);
    if (en) { const d = new Date(en); if (year > d.getFullYear() || (year === d.getFullYear() && month > d.getMonth() + 1)) return false; }
    return true;
  };

  const rows = employees
    .filter((e) => e.role !== "admin" && e.active !== false && e.in_pontaj !== false && empInMonth(e))
    .sort((a, b) => (parseInt(a.numar_contract) || 999999) - (parseInt(b.numar_contract) || 999999));

  const getCell = (emp, day) => {
    const c = data[emp.id]?.[String(day)];
    const val = c?.value;
    const hasDoc = c?.has_document === true;
    const hireDay = dayInMonth(emp.angajat_de_la, true);
    const exitDay = dayInMonth(emp.pontaj_pana_la, false);
    if (hireDay && day < hireDay) return { display: "X", cls: "bg-slate-100 text-slate-400", disabled: true, hasDoc: false };
    if (exitDay && day > exitDay) return { display: "X", cls: "bg-slate-100 text-slate-400", disabled: true, hasDoc: false };
    const weekend = isWeekend(day);
    const holiday = isHoliday(day);
    if (holiday) return { display: val || "", cls: val ? classFor(val) : "bg-violet-200 text-violet-700", disabled: false, hasDoc };
    if (weekend) return { display: val || "X", cls: val ? classFor(val) : "bg-slate-200 text-slate-500", disabled: false, hasDoc };
    if (val !== undefined && val !== null) return { display: val, cls: classFor(val), disabled: false, hasDoc };
    const isFuture = isFutureMonth || (isCurrentMonth && day > todayDate);
    if (isFuture) return { display: "", cls: "bg-slate-50", disabled: false, hasDoc: false };
    const norma = emp.norma_ore || 8;
    return { display: String(norma), cls: classFor(String(norma)), disabled: false, hasDoc };
  };

  const totalsFor = (emp) => {
    const hireDay = dayInMonth(emp.angajat_de_la, true);
    const exitDay = dayInMonth(emp.pontaj_pana_la, false);
    const norma = emp.norma_ore || 8;
    let ore = 0, co = 0, cm = 0, nm = 0, alt = 0, zl = 0;
    for (let d = 1; d <= daysInMonth; d++) {
      if (hireDay && d < hireDay) continue;
      if (exitDay && d > exitDay) continue;
      const weekend = isWeekend(d), holiday = isHoliday(d);
      const val = data[emp.id]?.[String(d)]?.value;
      const isFuture = isFutureMonth || (isCurrentMonth && d > todayDate);
      if (val === undefined || val === null) {
        if (!(weekend || holiday) && !isFuture) { ore += norma; zl += 1; }
        continue;
      }
      if (/^\d+$/.test(String(val))) {
        ore += parseInt(val);
        if (parseInt(val) > 0 && !(weekend || holiday)) zl += 1;
      } else if (val === "CO") co += 1;
      else if (val === "CM") cm += 1;
      else if (val === "NM") nm += 1;
      else if (val === "CFP" || val === "DEM" || val === "CNC" || val === "CD") alt += 1;
    }
    return { ore, co, cm, nm, alt, zl, tik: zl };
  };

  const goPrev = () => { if (month === 1) { setMonth(12); setYear(year - 1); } else setMonth(month - 1); };
  const goNext = () => { if (month === 12) { setMonth(1); setYear(year + 1); } else setMonth(month + 1); };
  const goCurrent = () => { setMonth(now.getMonth() + 1); setYear(now.getFullYear()); };

  const onCellClick = async (emp, day) => {
    const info = getCell(emp, day);
    if (info.disabled) return;
    if (editMode && canEdit) {
      const raw = data[emp.id]?.[String(day)]?.value ?? "";
      setCell({ emp, day, value: raw, fromDay: day, toDay: day });
      return;
    }
    // Mod vizualizare: deschide documentul dacă există, altfel anunță lipsa lui
    if (info.hasDoc) {
      await viewDoc(emp.id, day);
      return;
    }
    const raw = data[emp.id]?.[String(day)]?.value;
    if (raw && !/^\d+$/.test(String(raw))) {
      toast.info("Nu există document atașat pentru această zi");
    }
  };

  const applyDemisie = async () => {
    const { emp, day } = cell;
    try {
      await erpApi.post("/pontaj/demisie", { employee_id: emp.id, year, month, day });
      toast.success("Demisie înregistrată. Zilele rămase devin X, iar accesul la cont a fost blocat.");
      setCell(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const cancelDemisie = async () => {
    const { emp } = cell;
    try {
      await erpApi.post("/pontaj/anuleaza-demisie", { employee_id: emp.id, year, month });
      toast.success("Demisie anulată. Accesul la cont a fost redeschis.");
      setCell(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const saveRange = async () => {
    const { emp, value, fromDay, toDay } = cell;
    if (value === "DEM") { await applyDemisie(); return; }
    const lo = Math.min(Number(fromDay), Number(toDay));
    const hi = Math.max(Number(fromDay), Number(toDay));
    if (!lo || !hi || lo < 1 || hi > daysInMonth) { toast.error("Interval de zile invalid"); return; }
    const single = lo === hi;
    const entries = [];
    for (let d = lo; d <= hi; d++) {
      const info = getCell(emp, d);
      if (info.disabled) continue; // înainte de angajare / după ieșire
      if (!single && (isWeekend(d) || isHoliday(d))) continue;
      entries.push({ employee_id: emp.id, day: d, value });
    }
    if (entries.length === 0) { toast.error("Nicio zi validă în interval"); return; }
    try {
      await erpApi.post("/pontaj/bulk", { year, month, entries });
      setData((prev) => {
        const next = { ...prev };
        const empData = { ...(next[emp.id] || {}) };
        for (const e of entries) {
          const existing = { ...(empData[String(e.day)] || {}) };
          if (value === "") delete existing.value; else existing.value = value;
          empData[String(e.day)] = existing;
        }
        next[emp.id] = empData;
        return next;
      });
      toast.success(entries.length > 1 ? `Salvat pe ${entries.length} zile` : "Salvat");
      setCell(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const viewDoc = async (empId, day) => {
    try {
      const res = await erpApi.get(`/pontaj/document/${empId}/${year}/${month}/${day}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch {
      toast.error("Nu am putut deschide documentul");
    }
  };

  const uploadDoc = async (file) => {
    if (!file) return;
    const { emp, day } = cell;
    setDocBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await erpApi.post(`/pontaj/document?employee_id=${emp.id}&year=${year}&month=${month}&day=${day}`, fd);
      toast.success("Document atașat");
      setData((prev) => {
        const next = { ...prev };
        const empData = { ...(next[emp.id] || {}) };
        empData[String(day)] = { ...(empData[String(day)] || {}), has_document: true, document_filename: file.name };
        next[emp.id] = empData;
        return next;
      });
      setCell((c) => ({ ...c }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setDocBusy(false);
    }
  };

  const deleteDoc = async () => {
    const { emp, day } = cell;
    setDocBusy(true);
    try {
      await erpApi.delete(`/pontaj/document/${emp.id}/${year}/${month}/${day}`);
      setData((prev) => {
        const next = { ...prev };
        const empData = { ...(next[emp.id] || {}) };
        if (empData[String(day)]) empData[String(day)] = { ...empData[String(day)], has_document: false, document_filename: null };
        next[emp.id] = empData;
        return next;
      });
      toast.success("Document șters");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setDocBusy(false);
    }
  };

  const download = async (type) => {
    try {
      const res = await erpApi.get(`/pontaj/export/${type}/${year}/${month}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pontaj_${year}_${String(month).padStart(2, "0")}.${type === "pdf" ? "pdf" : "xlsx"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export eșuat");
    }
  };

  const openTichete = async () => {
    try { const r = await erpApi.get("/tichete/valoare"); setValoareTichet(r.data.valoare || 40); } catch { /* noop */ }
    setTichetDialog(true);
  };

  const exportTichete = async () => {
    try {
      await erpApi.post("/tichete/valoare", { valoare: Number(valoareTichet) });
      const res = await erpApi.get(`/tichete/export/${year}/${month}?valoare=${Number(valoareTichet)}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tichete_masa_${year}_${String(month).padStart(2, "0")}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      setTichetDialog(false);
      toast.success("Export tichete realizat");
    } catch {
      toast.error("Export eșuat");
    }
  };

  const openHolidays = async () => {
    setHolidayDialog(true);
    setNewHoliday((h) => ({ ...h, luna: String(month) }));
    try {
      const res = await erpApi.get(`/sarbatori/${year}`);
      setHolidayList(res.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const addHoliday = async () => {
    if (!newHoliday.zi || !newHoliday.luna || !newHoliday.nume) {
      toast.error("Completează zi, lună și denumire");
      return;
    }
    try {
      await erpApi.post("/sarbatori", {
        zi: parseInt(newHoliday.zi),
        luna: parseInt(newHoliday.luna),
        an: newHoliday.recurent ? null : year,
        nume: newHoliday.nume,
      });
      setNewHoliday({ zi: "", luna: String(month), nume: "", recurent: true });
      const res = await erpApi.get(`/sarbatori/${year}`);
      setHolidayList(res.data);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const removeHoliday = async (id) => {
    try {
      await erpApi.delete(`/sarbatori/${id}`);
      setHolidayList((l) => l.filter((h) => h.id !== id));
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const Legend = () => (
    <div className="flex flex-wrap gap-3 mb-4 p-3 bg-slate-50 rounded-lg text-xs" data-testid="pontaj-legend">
      {[
        ["2/4/6/8", "Ore lucrate", "bg-green-100 text-green-800 border-green-300"],
        ["CO", "Concediu odihnă", "bg-blue-100 text-blue-700 border-blue-300"],
        ["CM", "Concediu medical", "bg-orange-100 text-orange-700 border-orange-300"],
        ["CFP", "Fără plată", "bg-yellow-100 text-yellow-700 border-yellow-300"],
        ["NM", "Nemotivată", "bg-red-100 text-red-700 border-red-300"],
        ["DEM", "Demisie", "bg-purple-100 text-purple-700 border-purple-300"],
        ["CNC", "Naștere copil", "bg-pink-100 text-pink-700 border-pink-300"],
        ["CD", "Deces", "bg-stone-300 text-stone-700 border-stone-400"],
        ["WE", "Weekend", "bg-slate-200 text-slate-500 border-slate-400"],
        ["SL", "Sărbătoare", "bg-violet-200 text-violet-700 border-violet-400"],
      ].map(([t, l, c]) => (
        <div key={l} className="flex items-center gap-1.5">
          <span className={`inline-flex items-center justify-center min-w-7 h-5 px-1 rounded border text-[10px] font-semibold ${c}`}>{t}</span>
          <span className="text-slate-600">{l}</span>
        </div>
      ))}
      <div className="flex items-center gap-1.5">
        <span className="w-2 h-2 bg-[#2563EB] rounded-full" /> <span className="text-slate-600">Document atașat</span>
      </div>
    </div>
  );

  const valNum = Number(valoareTichet) || 0;
  const tichetSum = rows.reduce((a, e) => { const tk = totalsFor(e).tik; if (tk > 0) { a.ang += 1; a.tic += tk; } return a; }, { ang: 0, tic: 0 });

  return (
    <div className="space-y-4" data-testid="erp-pontaj-panel">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="icon" onClick={goPrev} data-testid="pontaj-prev-month" className="h-9 w-9"><ChevronLeft className="w-4 h-4" /></Button>
          <div className="flex items-center gap-2 px-2 py-1 bg-blue-50 rounded-lg border border-blue-200">
            <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v))}>
              <SelectTrigger className="w-[130px] h-8 border-0 bg-transparent" data-testid="pontaj-month-select"><SelectValue /></SelectTrigger>
              <SelectContent>{MONTHS_RO.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
              <SelectTrigger className="w-[84px] h-8 border-0 bg-transparent" data-testid="pontaj-year-select"><SelectValue /></SelectTrigger>
              <SelectContent>{[now.getFullYear() - 2, now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button variant="outline" size="icon" onClick={goNext} data-testid="pontaj-next-month" className="h-9 w-9"><ChevronRight className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" onClick={goCurrent} data-testid="pontaj-current-month" className="text-[#2563EB]">Luna curentă</Button>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={openHolidays} data-testid="pontaj-holidays-button" className="text-violet-700 border-violet-200 bg-violet-50 hover:bg-violet-100">
            <Star className="w-4 h-4 mr-1" /> Sărbători Legale
          </Button>
          {canExport && (
            <>
              <Button variant="outline" size="sm" onClick={openTichete} data-testid="pontaj-tichete-button" className="text-teal-700 border-teal-200 bg-teal-50 hover:bg-teal-100">
                <Ticket className="w-4 h-4 mr-1" /> Tichete masă
              </Button>
              <Button variant="outline" size="sm" onClick={() => download("excel")} data-testid="pontaj-export-excel" className="text-green-700 border-green-200 bg-green-50 hover:bg-green-100">
                <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
              </Button>
              <Button variant="outline" size="sm" onClick={() => download("pdf")} data-testid="pontaj-export-pdf" className="text-red-700 border-red-200 bg-red-50 hover:bg-red-100">
                <FileText className="w-4 h-4 mr-1" /> PDF
              </Button>
            </>
          )}
          {canEdit && (
            <Button
              size="sm"
              onClick={() => setEditMode((v) => !v)}
              data-testid="pontaj-edit-toggle"
              className={editMode ? "bg-green-600 hover:bg-green-700 text-white" : "bg-[#2563EB] hover:bg-[#1D4ED8] text-white"}
            >
              {editMode ? <><Check className="w-4 h-4 mr-1" /> Gata</> : <><Edit className="w-4 h-4 mr-1" /> Editare Pontaj</>}
            </Button>
          )}
        </div>
      </div>

      {editMode && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2 text-amber-800 text-sm" data-testid="pontaj-edit-banner">
          <Edit className="w-4 h-4" /> Mod editare activ — apasă pe orice celulă pentru a seta valoarea sau atașa un document.
        </div>
      )}

      <Legend />

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="pontaj-table-wrap">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr>
                <th className="sticky left-0 bg-slate-100 z-20 px-2 py-2 text-left font-semibold border-r w-8">Nr.</th>
                <th className="sticky left-8 bg-slate-100 z-20 px-2 py-2 text-left font-semibold border-r min-w-[160px]">Nume și prenume</th>
                {Array.from({ length: daysInMonth }, (_, i) => {
                  const day = i + 1;
                  const we = isWeekend(day), hol = isHoliday(day);
                  const d = new Date(year, month - 1, day).getDay();
                  return (
                    <th key={day} title={hol ? holidayDays[day] : ""} className={`px-1 py-1 text-center font-medium border-r min-w-[30px] ${hol ? "bg-violet-300 text-violet-900" : we ? "bg-slate-300 text-slate-600" : "bg-slate-100"}`}>
                      <div className="text-[9px] text-slate-500">{DAY_LETTERS[d]}</div>
                      <div>{day}</div>
                    </th>
                  );
                })}
                <th className="px-2 py-2 text-center font-semibold bg-green-100 border-l-2 border-green-300 min-w-[42px]">Total Ore</th>
                <th className="px-2 py-2 text-center font-semibold bg-blue-100 min-w-[34px]">CO</th>
                <th className="px-2 py-2 text-center font-semibold bg-orange-100 min-w-[34px]">CM</th>
                <th className="px-2 py-2 text-center font-semibold bg-red-100 min-w-[34px]">NM</th>
                <th className="px-2 py-2 text-center font-semibold bg-yellow-100 min-w-[42px]" title="CFP + DEM + CNC + CD">Altele</th>
                <th className="px-2 py-2 text-center font-semibold bg-purple-100 min-w-[34px]" title="Zile lucrate">ZL</th>
                <th className="px-2 py-2 text-center font-semibold bg-teal-100 min-w-[34px]" title="Tichete de masă">TIK</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={daysInMonth + 9} className="text-center py-10 text-slate-400">Niciun angajat în pontaj pentru această lună.</td></tr>
              ) : rows.map((emp, idx) => {
                const t = totalsFor(emp);
                return (
                  <tr key={emp.id} className="border-b hover:bg-slate-50" data-testid={`pontaj-row-${emp.id}`}>
                    <td className="sticky left-0 bg-white z-10 px-2 py-1.5 text-center border-r font-medium text-slate-500">{idx + 1}</td>
                    <td className="sticky left-8 bg-white z-10 px-2 py-1.5 border-r font-semibold text-[#0B1424] whitespace-nowrap">{displayName(emp)}</td>
                    {Array.from({ length: daysInMonth }, (_, i) => {
                      const day = i + 1;
                      const info = getCell(emp, day);
                      return (
                        <td
                          key={day}
                          onClick={() => onCellClick(emp, day)}
                          data-testid={`pontaj-cell-${emp.id}-${day}`}
                          className={`px-1 py-1 text-center border-r relative ${info.cls} ${info.disabled ? "" : "cursor-pointer hover:ring-2 hover:ring-[#2563EB] hover:ring-inset"}`}
                        >
                          {info.display}
                          {info.hasDoc && <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 bg-[#2563EB] rounded-full" />}
                        </td>
                      );
                    })}
                    <td className="px-2 py-1.5 text-center font-semibold bg-green-50 text-green-800 border-l-2 border-green-300">{t.ore || "-"}</td>
                    <td className="px-2 py-1.5 text-center font-semibold bg-blue-50 text-blue-700">{t.co || "-"}</td>
                    <td className="px-2 py-1.5 text-center font-semibold bg-orange-50 text-orange-700">{t.cm || "-"}</td>
                    <td className="px-2 py-1.5 text-center font-semibold bg-red-50 text-red-700">{t.nm || "-"}</td>
                    <td className="px-2 py-1.5 text-center font-semibold bg-yellow-50 text-yellow-700">{t.alt || "-"}</td>
                    <td className="px-2 py-1.5 text-center font-semibold bg-purple-50 text-purple-700">{t.zl || "-"}</td>
                    <td className="px-2 py-1.5 text-center font-semibold bg-teal-50 text-teal-700">{t.tik || "-"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Cell edit dialog */}
      <Dialog open={!!cell} onOpenChange={(o) => !o && setCell(null)}>
        <DialogContent className="max-w-md" data-testid="pontaj-cell-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <CalendarDays className="w-4 h-4 text-[#2563EB]" />
              {cell && `${displayName(cell.emp)} — ${cell.day} ${MONTHS_RO[month - 1]}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div>
              <Label className="text-xs font-semibold text-slate-600">1. Alege valoarea</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {PONTAJ_OPTIONS.map((o) => (
                  <Button
                    key={o.value}
                    variant={cell?.value === o.value ? "default" : "outline"}
                    size="sm"
                    data-testid={`pontaj-value-${o.value}`}
                    onClick={() => setCell({ ...cell, value: o.value })}
                    className={cell?.value === o.value ? "bg-[#2563EB] hover:bg-[#1D4ED8]" : ""}
                  >
                    {o.value}
                  </Button>
                ))}
                <Button
                  variant={cell?.value === "" ? "default" : "outline"}
                  size="sm"
                  data-testid="pontaj-value-clear"
                  onClick={() => setCell({ ...cell, value: "" })}
                  className={cell?.value === "" ? "col-span-3 bg-slate-600 hover:bg-slate-700 text-white" : "col-span-3 text-slate-600"}
                >
                  Golește (revine la normă)
                </Button>
              </div>
              <p className="text-[11px] text-slate-400 mt-2">8/6/4/2 = ore · CO · CM · CFP · NM · DEM · CNC · CD</p>
            </div>

            {cell?.value === "DEM" ? (
              <div className="border-t pt-3">
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800" data-testid="pontaj-demisie-warning">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-semibold">Înregistrezi demisia pe ziua {cell.day} {MONTHS_RO[month - 1]}.</p>
                    <ul className="mt-1 space-y-0.5 text-amber-700 text-[13px]">
                      <li>• Zilele rămase din lună devin <strong>X</strong> (nelucrate)</li>
                      <li>• Angajatul primește data de ieșire din pontaj</li>
                      <li>• <strong>Accesul la cont este blocat</strong> din ziua demisiei</li>
                    </ul>
                  </div>
                </div>
              </div>
            ) : (
              <div className="border-t pt-3">
                <Label className="text-xs font-semibold text-slate-600">2. Aplică pe interval de zile</Label>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <span className="text-sm text-slate-500">De la ziua</span>
                  <Input type="number" min="1" max={daysInMonth} value={cell?.fromDay ?? ""} data-testid="pontaj-from-day"
                    onChange={(e) => setCell({ ...cell, fromDay: e.target.value })} className="w-20 h-8 text-center" />
                  <span className="text-sm text-slate-500">până la</span>
                  <Input type="number" min="1" max={daysInMonth} value={cell?.toDay ?? ""} data-testid="pontaj-to-day"
                    onChange={(e) => setCell({ ...cell, toDay: e.target.value })} className="w-20 h-8 text-center" />
                </div>
                <p className="text-[11px] text-slate-400 mt-1">Pe interval (mai mult de o zi) se sar automat weekendurile și sărbătorile.</p>
              </div>
            )}

            <div className="border-t pt-3">
              <Label className="text-xs font-semibold text-slate-600 flex items-center gap-1.5"><Paperclip className="w-3.5 h-3.5" /> Document justificativ</Label>
              {cell && data[cell.emp.id]?.[String(cell.day)]?.has_document ? (
                <div className="flex items-center gap-2 mt-2">
                  <Button variant="outline" size="sm" onClick={() => viewDoc(cell.emp.id, cell.day)} data-testid="pontaj-doc-view" className="flex-1 text-[#2563EB]">
                    <Eye className="w-4 h-4 mr-1" /> Vezi document
                  </Button>
                  <Button variant="outline" size="sm" onClick={deleteDoc} disabled={docBusy} data-testid="pontaj-doc-delete" className="text-red-600">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="mt-2">
                  <Input type="file" disabled={docBusy} data-testid="pontaj-doc-upload" onChange={(e) => uploadDoc(e.target.files?.[0])} className="text-xs" />
                  {docBusy && <p className="text-xs text-slate-400 mt-1 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Se încarcă...</p>}
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="gap-2">
            {cell && data[cell.emp.id]?.[String(cell.day)]?.value === "DEM" ? (
              <>
                <Button variant="outline" onClick={cancelDemisie} data-testid="pontaj-demisie-cancel-btn" className="mr-auto border-green-300 text-green-700 hover:bg-green-50">
                  <RotateCcw className="w-4 h-4 mr-1" /> Anulează demisia
                </Button>
                <Button variant="outline" onClick={() => setCell(null)} data-testid="pontaj-cancel">Închide</Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setCell(null)} data-testid="pontaj-cancel">Anulează</Button>
                {cell?.value === "DEM" ? (
                  <Button onClick={saveRange} data-testid="pontaj-save" className="bg-amber-600 hover:bg-amber-700 text-white">
                    <UserMinus className="w-4 h-4 mr-1" /> Înregistrează demisia
                  </Button>
                ) : (
                  <Button onClick={saveRange} data-testid="pontaj-save" className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white">
                    <Check className="w-4 h-4 mr-1" /> Salvează
                  </Button>
                )}
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Holidays dialog */}
      <Dialog open={holidayDialog} onOpenChange={setHolidayDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="pontaj-holidays-dialog">
          <DialogHeader><DialogTitle>Sărbători legale {year}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            {canEdit && (
              <div className="p-3 bg-slate-50 rounded-lg space-y-2">
                <label className="flex items-center gap-2 text-xs text-slate-600">
                  <input type="checkbox" checked={newHoliday.recurent} onChange={(e) => setNewHoliday({ ...newHoliday, recurent: e.target.checked })} data-testid="holiday-recurent" />
                  Recurentă în fiecare an (altfel doar pentru {year})
                </label>
                <div className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-2">
                    <Label className="text-[11px]">Zi</Label>
                    <Input type="number" min="1" max="31" value={newHoliday.zi} onChange={(e) => setNewHoliday({ ...newHoliday, zi: e.target.value })} data-testid="holiday-day" className="h-8 mt-1" />
                  </div>
                  <div className="col-span-3">
                    <Label className="text-[11px]">Luna</Label>
                    <Select value={newHoliday.luna} onValueChange={(v) => setNewHoliday({ ...newHoliday, luna: v })}>
                      <SelectTrigger className="h-8 mt-1" data-testid="holiday-month"><SelectValue /></SelectTrigger>
                      <SelectContent>{MONTHS_RO.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-5">
                    <Label className="text-[11px]">Denumire</Label>
                    <Input value={newHoliday.nume} onChange={(e) => setNewHoliday({ ...newHoliday, nume: e.target.value })} data-testid="holiday-name" className="h-8 mt-1" placeholder="ex: Zi liberă firmă" />
                  </div>
                  <div className="col-span-2">
                    <Button size="sm" onClick={addHoliday} data-testid="holiday-add" className="w-full bg-[#2563EB] hover:bg-[#1D4ED8]">Adaugă</Button>
                  </div>
                </div>
              </div>
            )}
            <div className="divide-y border rounded-lg">
              {holidayList.map((h) => (
                <div key={h.id} className="flex items-center justify-between px-3 py-2 text-sm">
                  <div>
                    <span className="font-semibold text-[#0B1424]">{h.zi} {MONTHS_RO[h.luna - 1]}</span>
                    <span className="text-slate-500 ml-2">{h.nume}</span>
                    <span className="text-[10px] uppercase ml-2 px-1.5 py-0.5 rounded bg-slate-100 text-slate-400">{h.tip}</span>
                  </div>
                  {h.editable && canEdit && (
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeHoliday(h.id)} data-testid={`holiday-delete-${h.id}`}>
                      <Trash2 className="w-3.5 h-3.5 text-red-500" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tichete masă dialog */}
      <Dialog open={tichetDialog} onOpenChange={setTichetDialog}>
        <DialogContent className="max-w-md" data-testid="pontaj-tichete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Ticket className="w-4 h-4 text-teal-600" /> Tichete de masă — {MONTHS_RO[month - 1]} {year}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div>
              <Label className="text-xs font-semibold text-slate-600">Valoare unitară tichet (RON)</Label>
              <Input type="number" min="0" step="0.5" value={valoareTichet} onChange={(e) => setValoareTichet(e.target.value)} data-testid="tichet-valoare" className="mt-1" />
            </div>
            <div className="bg-slate-50 rounded-lg p-3 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-slate-500">Angajați cu tichete:</span><span className="font-semibold" data-testid="tichet-angajati">{tichetSum.ang}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Total tichete:</span><span className="font-semibold" data-testid="tichet-total">{tichetSum.tic}</span></div>
              <div className="flex justify-between border-t pt-1 mt-1"><span className="text-slate-500">Valoare totală:</span><span className="font-bold text-teal-700" data-testid="tichet-valoare-totala">{(tichetSum.tic * valNum).toFixed(2)} RON</span></div>
            </div>
            <p className="text-[11px] text-slate-400">Tichetele se acordă pentru zilele efectiv lucrate (fără weekend, sărbători, CO, CM, NM, CFP, DEM, CNC, CD).</p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setTichetDialog(false)}>Închide</Button>
            <Button onClick={exportTichete} data-testid="tichete-export" className="bg-teal-600 hover:bg-teal-700 text-white"><FileSpreadsheet className="w-4 h-4 mr-1" /> Export Excel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
